/**
 * assets/launch.js : launch page template (sections/launch-*.liquid)
 *
 * One script for every launch-* section. Loaded by each section with defer and
 * guarded so it initializes once however many sections are on the page.
 *
 * What it does
 *  - Scroll reveals, macro count-up and the story image pan, all gated on
 *    prefers-reduced-motion: no-preference. Without JS the page is static
 *    and fully visible (see launch.css).
 *  - Hero video: play, pause control, poster under reduced motion.
 *  - Purchase module state: the segmented control drives the one Skio plan
 *    picker through its public methods (selectSellingPlanGroup /
 *    selectSellingPlan), listens for its pdp:plan-change event, and keeps
 *    quantity, tier strip, prices, CTA label, sticky bar and mirrors in sync.
 *
 * What it never does
 *  - POST /cart/add.js for the single product. That is assets/aloha-cart.js
 *    (drawer, via the form submit) or assets/acq-direct-checkout.js (direct,
 *    via the data-acq-direct-checkout button). A second fetch here would
 *    double-add.
 *  - Write price text into .skio-price--* elements. The picker owns those.
 *
 * The bundle path is the one place this file adds to cart: two lines with
 * the BYOB Discount Function properties, then cart:refresh + cart:open (or
 * /checkout in direct mode). The bundle button is type="button" outside the
 * form so AlohaCart never sees it.
 */
(function () {
  'use strict';

  if (window.__alohaLaunch) return;
  window.__alohaLaunch = true;

  document.documentElement.classList.add('js-launch');

  var reduced = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var hasIO = 'IntersectionObserver' in window;

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function fallbackMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function uuid() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  /* ---- Reveals ---------------------------------------------------------- */

  function initReveal() {
    var els = document.querySelectorAll('.launch-reveal');
    if (!els.length) return;
    if (reduced || !hasIO) {
      els.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        en.target.classList.add('is-in');
        io.unobserve(en.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    els.forEach(function (el) { io.observe(el); });

    // Safety net: nothing on the page may stay hidden because an observer never
    // fired (background tab at load, embedded previews, odd scroll containers).
    setTimeout(function () {
      els.forEach(function (el) { el.classList.add('is-in'); });
      io.disconnect();
    }, 6000);
  }

  /* ---- Macro count-up --------------------------------------------------- */

  function initCountUp() {
    var wraps = document.querySelectorAll('[data-launch-countup]');
    if (!wraps.length || reduced || !hasIO) return;
    wraps.forEach(function (wrap) {
      var values = wrap.querySelectorAll('.pdp-spec-strip__value');
      if (!values.length) return;
      var io = new IntersectionObserver(function (entries) {
        if (!entries[0].isIntersecting) return;
        io.disconnect();
        values.forEach(function (el) {
          var m = /^(\d+(?:\.\d+)?)(.*)$/.exec(el.textContent.trim());
          if (!m) return;
          var end = parseFloat(m[1]);
          var suffix = m[2];
          var decimals = (m[1].split('.')[1] || '').length;
          var start = null;
          el.textContent = (0).toFixed(decimals) + suffix;
          function step(ts) {
            if (start === null) start = ts;
            var p = Math.min(1, (ts - start) / 900);
            var eased = 1 - Math.pow(1 - p, 3);
            el.textContent = (end * eased).toFixed(decimals) + suffix;
            if (p < 1) requestAnimationFrame(step);
            else el.textContent = m[1] + suffix;
          }
          requestAnimationFrame(step);
        });
      }, { threshold: 0.4 });
      io.observe(wrap);
    });
  }

  /* ---- Hero video ------------------------------------------------------- */

  function initHero() {
    document.querySelectorAll('[data-launch-hero]').forEach(function (hero) {
      var video = hero.querySelector('[data-launch-video]');
      var btn = hero.querySelector('[data-launch-video-toggle]');
      hero.classList.add('is-ready');

      // Scroll-linked opening: --launch-hero-p runs 0 → 1 across the hero's
      // own height. CSS does the rest (see .launch-hero[data-launch-hero-scroll]).
      if (hero.hasAttribute('data-launch-hero-scroll') && !reduced) {
        var heroTick = false;
        function measureHero() {
          heroTick = false;
          var travel = Math.max(1, hero.offsetHeight - 120);
          var p = Math.min(1, Math.max(0, window.scrollY / travel));
          hero.style.setProperty('--launch-hero-p', p.toFixed(3));
        }
        function onHeroScroll() {
          if (heroTick) return;
          heroTick = true;
          requestAnimationFrame(measureHero);
        }
        window.addEventListener('scroll', onHeroScroll, { passive: true });
        window.addEventListener('resize', onHeroScroll);
        measureHero();
      }
      if (!video) return;

      if (reduced) {
        video.removeAttribute('autoplay');
        try { video.pause(); } catch (e) { /* no-op */ }
        if (btn) btn.hidden = true;
        return;
      }

      var attempt = video.play();
      if (attempt && attempt.catch) attempt.catch(function () { /* autoplay blocked: poster stays */ });

      if (!btn) return;
      btn.addEventListener('click', function () {
        if (video.paused) {
          video.play();
          btn.setAttribute('aria-pressed', 'false');
          btn.setAttribute('aria-label', 'Pause video');
        } else {
          video.pause();
          btn.setAttribute('aria-pressed', 'true');
          btn.setAttribute('aria-label', 'Play video');
        }
      });
    });
  }

  /* ---- Story image pan -------------------------------------------------- */

  function initParallax() {
    var frames = document.querySelectorAll('[data-launch-parallax]');
    if (!frames.length || reduced) return;
    var ticking = false;

    function update() {
      ticking = false;
      var vh = window.innerHeight || 1;
      frames.forEach(function (frame) {
        var r = frame.getBoundingClientRect();
        if (r.bottom < 0 || r.top > vh) return;
        var img = frame.querySelector('.launch-story__img');
        if (!img) return;
        // -1 when the frame's center is a viewport below center, +1 when above.
        var progress = (r.top + r.height / 2 - vh / 2) / (vh / 2 + r.height / 2);
        var clamped = Math.max(-1, Math.min(1, progress));
        img.style.transform = 'translateY(' + (clamped * -8).toFixed(2) + '%)';
      });
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    update();
  }

  /* ---- Purchase module -------------------------------------------------- */

  function initPurchase() {
    var section = document.querySelector('[data-launch-purchase]');
    if (!section) return;

    var d = section.dataset;
    var buy = section.querySelector('[data-launch-buy]');
    var picker = section.querySelector('[data-launch-picker]');
    var form = section.querySelector('form.launch-form');
    var variantSelect = form ? form.querySelector('select[name="id"]') : null;
    var qtyInput = form ? form.querySelector('.quantity-selector') : null;
    var cta = section.querySelector('[data-launch-cta]');
    var ctaLabel = cta ? cta.querySelector('[data-launch-cta-label]') : null;
    var ctaPrice = cta ? cta.querySelector('[data-launch-cta-price]') : null;
    var totalEl = section.querySelector('[data-launch-total]');
    var saveEl = section.querySelector('[data-launch-save]');
    var segs = section.querySelectorAll('[data-launch-seg]');
    var panes = section.querySelectorAll('[data-launch-pane]');
    var tiers = section.querySelectorAll('[data-launch-tier]');
    var freqs = section.querySelectorAll('[data-launch-freq]');
    var singleRow = section.querySelector('[data-launch-single]');
    var bundlePane = section.querySelector('[data-launch-pane="bundle"]');
    var bundleCta = section.querySelector('[data-launch-bundle-cta]');
    var errorEl = section.querySelector('[data-launch-error]');
    var sticky = section.querySelector('[data-launch-sticky]');

    var thumb = section.querySelector('.launch-seg__thumb');
    var lastPer = null;

    function positionThumb() {
      if (!thumb) return;
      var sel = section.querySelector('[data-launch-seg][aria-selected="true"]');
      if (!sel) { thumb.style.width = '0px'; return; }
      thumb.style.left = sel.offsetLeft + 'px';
      thumb.style.width = sel.offsetWidth + 'px';
      if (!thumb.classList.contains('is-ready')) {
        requestAnimationFrame(function () { thumb.classList.add('is-ready'); });
      }
    }

    function tick(nodes) {
      if (reduced) return;
      nodes.forEach(function (n) {
        if (!n) return;
        n.classList.add('is-tick');
        setTimeout(function () { n.classList.remove('is-tick'); }, 300);
      });
    }

    var available = d.available === 'true';
    var hasPlans = d.hasPlans === 'true' && !!picker;
    var mode = (d.defaultMode === 'autoship' && hasPlans) ? 'autoship' : 'onetime';
    var pickerReady = false;
    var busy = false;

    function money(cents) {
      if (picker && typeof picker.money === 'function') {
        try { return picker.money(cents); } catch (e) { /* fall through */ }
      }
      return fallbackMoney(cents);
    }

    function unitPrice() {
      if (variantSelect) {
        var opt = variantSelect.options[variantSelect.selectedIndex];
        var p = opt ? parseInt(opt.getAttribute('data-price'), 10) : NaN;
        if (!isNaN(p)) return p;
      }
      if (picker && picker.selectedVariant) return picker.selectedVariant.price;
      return 0;
    }

    function qty() {
      if (!qtyInput) return 1;
      var n = parseInt(qtyInput.value, 10);
      if (isNaN(n) || n < 1) { n = 1; qtyInput.value = '1'; }
      return n;
    }

    function units(n) {
      return n === 1 ? (d.unitSingular || 'box') : (d.unitPlural || 'boxes');
    }

    function planPrice() {
      if (mode === 'autoship' && picker && picker.selectedSellingPlan && typeof picker.price === 'function') {
        try {
          var p = picker.price(picker.selectedSellingPlan, false);
          if (typeof p === 'number' && !isNaN(p)) return Math.round(p);
        } catch (e) { /* fall through */ }
      }
      return unitPrice();
    }

    function autoshipGroup() {
      if (!picker || !picker.availableSellingPlanGroups || !picker.availableSellingPlanGroups.length) return null;
      return picker.availableSellingPlanGroups[0];
    }

    function showError(msg) {
      if (!errorEl) return;
      errorEl.textContent = msg;
      errorEl.hidden = false;
    }

    function clearError() {
      if (!errorEl) return;
      errorEl.hidden = true;
      errorEl.textContent = '';
    }

    function setMirror(name, text, show) {
      section.querySelectorAll('[data-launch-mirror="' + name + '"]').forEach(function (el) {
        el.textContent = text;
        if (typeof show === 'boolean') el.hidden = !show;
      });
    }

    function render() {
      var n = qty();
      var base = unitPrice();

      segs.forEach(function (seg) {
        seg.setAttribute('aria-selected', seg.getAttribute('data-launch-seg') === mode ? 'true' : 'false');
      });
      positionThumb();
      panes.forEach(function (pane) {
        pane.hidden = pane.getAttribute('data-launch-pane') !== mode;
      });

      var isBundle = mode === 'bundle' && bundlePane;
      if (singleRow) singleRow.hidden = !!isBundle;

      if (isBundle) {
        var mainPrice = parseInt(bundlePane.getAttribute('data-main-price'), 10) || base;
        var partnerPrice = parseInt(bundlePane.getAttribute('data-partner-price'), 10) || 0;
        var pct = parseFloat(bundlePane.getAttribute('data-bundle-pct')) || 0;
        var full = mainPrice + partnerPrice;
        var priced = Math.round(full * (1 - pct / 100));
        var totalNode = bundlePane.querySelector('[data-launch-bundle-total]');
        var wasNode = bundlePane.querySelector('[data-launch-bundle-was]');
        var saveNode = bundlePane.querySelector('[data-launch-bundle-save]');
        var bCtaPrice = bundlePane.querySelector('[data-launch-bundle-cta-price]');
        if (totalNode) totalNode.textContent = money(priced);
        if (wasNode) { wasNode.textContent = money(full); wasNode.hidden = pct <= 0; }
        if (saveNode) { saveNode.textContent = 'Save ' + money(full - priced); saveNode.hidden = pct <= 0; }
        if (bCtaPrice) bCtaPrice.textContent = money(priced);
        if (totalEl) totalEl.hidden = true;

        var bundleLabel = bundleCta && bundleCta.disabled ? (d.ctaSoldout || 'Sold Out') : (d.ctaBundle || 'Add Bundle');
        setMirror('cta', bundleLabel);
        setMirror('mode', d.labelBundle || 'Bundle', true);
        if (sticky) {
          var skioNow = sticky.querySelector('[data-launch-sticky-price]');
          var bundleNow = sticky.querySelector('[data-launch-sticky-bundle]');
          var was = sticky.querySelector('[data-launch-sticky-was]');
          if (skioNow) skioNow.hidden = true;
          if (bundleNow) { bundleNow.textContent = money(priced); bundleNow.hidden = false; }
          if (was) was.hidden = true;
        }
        return;
      }

      var per = planPrice();
      var save = mode === 'autoship' ? Math.max(0, base - per) : 0;
      if (lastPer !== null && lastPer !== per) {
        tick([section.querySelector('[data-launch-pane]:not([hidden]) .launch-price__now'), ctaPrice]);
      }
      lastPer = per;

      var label = available
        ? (mode === 'autoship' ? (d.ctaAutoship || 'Start Autoship') : (d.ctaOnetime || 'Buy Now'))
        : (d.ctaSoldout || 'Sold Out');
      if (ctaLabel && !busy) ctaLabel.textContent = label;
      if (ctaPrice) ctaPrice.textContent = available ? money(per) : '';

      if (totalEl) {
        if (n > 1) {
          totalEl.textContent = 'Total ' + money(per * n) + ' for ' + n + ' ' + units(n);
          totalEl.hidden = false;
        } else {
          totalEl.hidden = true;
        }
      }

      if (saveEl) {
        if (save > 0) {
          saveEl.textContent = 'Save ' + money(save) + ' per ' + units(1);
          saveEl.hidden = false;
        } else {
          saveEl.hidden = true;
        }
      }

      var activeTier = n >= 3 ? 3 : n;
      tiers.forEach(function (tier) {
        var t = parseInt(tier.getAttribute('data-launch-tier'), 10);
        var pct = parseFloat(tier.getAttribute('data-launch-pct')) || 0;
        tier.classList.toggle('is-active', t === activeTier);
        tier.setAttribute('aria-current', t === activeTier ? 'true' : 'false');
        var saveNode = tier.querySelector('[data-launch-tier-save]');
        if (saveNode) saveNode.textContent = pct > 0 ? 'Save ' + money(Math.round(base * pct / 100)) + ' each' : '';
      });

      if (cta && cta.hasAttribute('data-acq-direct-checkout')) {
        cta.setAttribute('data-acq-quantity', String(n));
        if (variantSelect) cta.setAttribute('data-acq-variant-id', variantSelect.value);
      }

      setMirror('cta', label);
      setMirror('mode', mode === 'autoship' ? (d.labelAutoship || 'Autoship') : (d.labelOnetime || 'One-time'), mode === 'autoship');
      if (sticky) {
        var stickyNow = sticky.querySelector('[data-launch-sticky-price]');
        var stickyBundle = sticky.querySelector('[data-launch-sticky-bundle]');
        var stickyWas = sticky.querySelector('[data-launch-sticky-was]');
        if (stickyNow) stickyNow.hidden = false;
        if (stickyBundle) stickyBundle.hidden = true;
        if (stickyWas) stickyWas.hidden = !(mode === 'autoship' && save > 0);
      }
    }

    function syncFrequency() {
      if (!picker || !picker.selectedSellingPlan) return;
      var id = String(picker.selectedSellingPlan.id);
      freqs.forEach(function (input) { input.checked = String(input.value) === id; });
    }

    function setMode(next) {
      if (next === 'bundle' && !bundlePane) next = 'onetime';
      if (next === 'autoship' && !hasPlans) next = 'onetime';
      mode = next;
      clearError();
      if (picker && pickerReady && mode !== 'bundle') {
        try {
          if (mode === 'autoship') {
            var group = autoshipGroup();
            if (group) picker.selectSellingPlanGroup(group);
          } else {
            picker.selectSellingPlanGroup(null);
          }
        } catch (e) { /* picker not ready: render from static prices */ }
      }
      render();
    }

    function disableAutoship() {
      hasPlans = false;
      segs.forEach(function (seg) {
        if (seg.getAttribute('data-launch-seg') === 'autoship') seg.remove();
      });
      var segWrap = section.querySelector('.launch-seg');
      if (segWrap) {
        var remaining = segWrap.querySelectorAll('[data-launch-seg]').length;
        segWrap.style.setProperty('--launch-seg-count', String(remaining));
        if (remaining < 2) segWrap.hidden = true;
      }
      segs = section.querySelectorAll('[data-launch-seg]');
      var autoPane = section.querySelector('[data-launch-pane="autoship"]');
      if (autoPane) autoPane.remove();
      panes = section.querySelectorAll('[data-launch-pane]');
      if (mode === 'autoship') mode = 'onetime';
    }

    function finishLoading() {
      if (buy) buy.classList.remove('is-loading');
    }

    function onPickerReady() {
      pickerReady = true;
      if (!autoshipGroup()) {
        // Product is not attached to a Skio selling plan group: no autoship path.
        disableAutoship();
      }
      setMode(mode);
      syncFrequency();
      finishLoading();
    }

    function waitForPicker() {
      if (!hasPlans) { onPickerReady(); return; }
      var tries = 0;
      (function poll() {
        var groups = picker.availableSellingPlanGroups;
        if (groups !== undefined && picker.selectedVariant) { onPickerReady(); return; }
        if (++tries > 40) { pickerReady = true; setMode(mode); finishLoading(); return; }
        setTimeout(poll, 100);
      })();
    }

    // Events -----------------------------------------------------------------

    segs.forEach(function (seg) {
      seg.addEventListener('click', function () { setMode(seg.getAttribute('data-launch-seg')); });
    });

    tiers.forEach(function (tier) {
      tier.addEventListener('click', function () {
        if (!qtyInput) return;
        qtyInput.value = tier.getAttribute('data-launch-tier');
        qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });

    freqs.forEach(function (input) {
      input.addEventListener('change', function () {
        if (!picker || !input.checked) return;
        try {
          if (!picker.selectedSellingPlanGroup) {
            var group = autoshipGroup();
            if (group) picker.selectSellingPlanGroup(group);
          }
          picker.selectSellingPlan(input.value);
        } catch (e) { /* no-op */ }
        render();
      });
    });

    if (qtyInput) {
      qtyInput.addEventListener('change', render);
      qtyInput.addEventListener('input', render);
    }

    if (variantSelect) {
      variantSelect.addEventListener('change', render);
    }

    window.addEventListener('resize', positionThumb);

    // The picker announces every plan or variant change. Keep the segments,
    // pills and prices in step with it (this is also what makes plan_ui =
    // "skio" work: the picker's own cards move the segments).
    document.addEventListener('pdp:plan-change', function (e) {
      if (mode === 'bundle') return;
      if (e.detail && typeof e.detail.isSubscription === 'boolean') {
        mode = e.detail.isSubscription ? 'autoship' : 'onetime';
      }
      syncFrequency();
      render();
    });

    // Drawer mode: AlohaCart handles the submit. Give the button feedback only.
    if (form && cta && cta.getAttribute('type') === 'submit') {
      form.addEventListener('submit', function () {
        if (!ctaLabel) return;
        busy = true;
        ctaLabel.textContent = 'Adding…';
        setTimeout(function () { busy = false; render(); }, 6000);
      });
      document.addEventListener('CartUpdated', function () {
        busy = false;
        render();
      });
    }

    // Bundle: the one add this file performs.
    if (bundleCta && bundlePane) {
      bundleCta.addEventListener('click', function () {
        if (bundleCta.disabled || busy) return;
        var labelNode = bundleCta.querySelector('[data-launch-cta-label]');
        var original = labelNode ? labelNode.textContent : '';
        busy = true;
        clearError();
        if (labelNode) labelNode.textContent = 'Adding…';

        var bundleId = uuid();
        var items = [
          { id: parseInt(bundlePane.getAttribute('data-main-variant'), 10), quantity: 1, properties: { _byob_bundle_id: bundleId, _byob_qty: '2' } },
          { id: parseInt(bundlePane.getAttribute('data-partner-variant'), 10), quantity: 1, properties: { _byob_bundle_id: bundleId, _byob_qty: '2' } }
        ];

        function reset() {
          busy = false;
          if (labelNode) labelNode.textContent = original;
        }

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: items })
        })
          .then(function (r) {
            if (!r.ok) return r.json().then(function (err) { throw new Error(err.description || 'We could not add that bundle. Nothing was charged.'); });
            return r.json();
          })
          .then(function () {
            return fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ attributes: { _byob_onetime: bundleId } })
            }).catch(function () { /* tracking is best-effort */ });
          })
          .then(function () {
            if (d.checkoutMode === 'direct') {
              window.location.href = '/checkout';
              return;
            }
            document.dispatchEvent(new CustomEvent('cart:refresh'));
            document.dispatchEvent(new CustomEvent('cart:open'));
            reset();
          })
          .catch(function (err) {
            showError(err.message || 'We could not add that to your cart. Check your connection and try again. Nothing was charged.');
            reset();
          });
      });
    }

    // Sticky bar: proxy the active CTA; show once the buy column scrolls out,
    // hide while the footer is in view.
    if (sticky) {
      var stickyBtn = sticky.querySelector('[data-launch-sticky-cta]');
      if (stickyBtn) {
        stickyBtn.addEventListener('click', function () {
          var target = mode === 'bundle' ? bundleCta : cta;
          if (!target || target.disabled) {
            section.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
            return;
          }
          target.click();
        });
      }

      var pastBuy = false;
      var footerVisible = false;
      function applySticky() {
        var show = pastBuy && !footerVisible;
        sticky.classList.toggle('is-visible', show);
        sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
      }

      // Scroll-driven rather than IntersectionObserver: a jump from below
      // the buy column to the top of the page is "not intersecting" on both
      // sides, so an observer never fires and the bar would stay up over
      // the hero.
      if (buy) {
        var stickyTick = false;
        function measureSticky() {
          stickyTick = false;
          pastBuy = buy.getBoundingClientRect().bottom < 0;
          applySticky();
        }
        function onStickyScroll() {
          if (stickyTick) return;
          stickyTick = true;
          requestAnimationFrame(measureSticky);
        }
        window.addEventListener('scroll', onStickyScroll, { passive: true });
        window.addEventListener('resize', onStickyScroll);
        measureSticky();

        var footer = document.querySelector('footer, .site-footer, #shopify-section-footer');
        if (footer && hasIO) {
          new IntersectionObserver(function (entries) {
            footerVisible = entries[0].isIntersecting;
            applySticky();
          }, { threshold: 0.05 }).observe(footer);
        }
      } else {
        pastBuy = true;
        applySticky();
      }
    }

    // The picker re-prices asynchronously after its Skio rule lookup (cart-aware
    // tiers) and writes the result into .skio-price--* without an event. Watch
    // the autoship price node so the CTA, savings and totals follow it.
    var priceNode = section.querySelector('[data-launch-pane="autoship"] .skio-price--discounted');
    if (priceNode && 'MutationObserver' in window) {
      new MutationObserver(function () { render(); }).observe(priceNode, { childList: true, characterData: true, subtree: true });
    }

    // Boot ---------------------------------------------------------------------
    render();
    waitForPicker();
    setTimeout(finishLoading, 3000);
  }

  ready(function () {
    initReveal();
    initCountUp();
    initHero();
    initParallax();
    initPurchase();
  });
})();
